import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowRight, Captions, ChevronLeft, Heart, Plus, Search, Share2, Volume2, VolumeX, } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isInProgressGameScore } from "../domain/game-state";
import { GameScoreBug } from "./GameScoreBug";
function firstTeamCode(scoreLockup) {
    const code = scoreLockup?.match(/^[A-Z]{2,3}/)?.[0];
    return code ?? "NBA";
}
export function VerticalClipPlayer({ companion, deviceTime, initialMuted = true, item, layer = "base", onClose, onGoToGame, onOpenGame, }) {
    const closeButtonRef = useRef(null);
    const dialogRef = useRef(null);
    const touchStartYRef = useRef(null);
    const [isClosing, setIsClosing] = useState(false);
    const [isMuted, setIsMuted] = useState(initialMuted);
    const clipStream = useMemo(() => {
        const companionClips = companion?.items.filter((candidate) => candidate.display.media?.type === "video") ?? [];
        if (companionClips.some((candidate) => candidate.display.id === item.display.id)) {
            return companionClips;
        }
        return [item, ...companionClips];
    }, [companion, item]);
    const initialIndex = Math.max(0, clipStream.findIndex((candidate) => candidate.display.id === item.display.id));
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const currentItem = clipStream[currentIndex] ?? item;
    const { display } = currentItem;
    const teamCode = useMemo(() => firstTeamCode(display.scoreLockup), [display.scoreLockup]);
    const gameScore = companion?.score ?? display.scoreLockup;
    const isLiveGame = Boolean(companion &&
        isInProgressGameScore(companion.score));
    const showPreviousClip = useCallback(() => {
        setCurrentIndex((index) => index <= 0 ? clipStream.length - 1 : index - 1);
    }, [clipStream.length]);
    const showNextClip = useCallback(() => {
        setCurrentIndex((index) => index >= clipStream.length - 1 ? 0 : index + 1);
    }, [clipStream.length]);
    useEffect(() => {
        const selectedIndex = clipStream.findIndex((candidate) => candidate.display.id === item.display.id);
        setCurrentIndex(Math.max(0, selectedIndex));
    }, [clipStream, item.display.id]);
    const requestClose = useCallback(() => {
        if (isClosing) {
            return;
        }
        if (typeof window !== "undefined" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            onClose();
            return;
        }
        setIsClosing(true);
    }, [isClosing, onClose]);
    useEffect(() => {
        if (!isClosing) {
            return;
        }
        const timeout = window.setTimeout(onClose, 220);
        return () => window.clearTimeout(timeout);
    }, [isClosing, onClose]);
    useEffect(() => {
        closeButtonRef.current?.focus();
        function handleKeyDown(event) {
            if (event.key === "Escape") {
                requestClose();
            }
            if (event.key === "ArrowUp") {
                showPreviousClip();
            }
            if (event.key === "ArrowDown") {
                showNextClip();
            }
            if (event.key === "Tab") {
                const focusable = [
                    ...(dialogRef.current?.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') ?? []),
                ];
                if (focusable.length === 0) {
                    event.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                }
                else if (!event.shiftKey &&
                    document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        }
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [requestClose, showNextClip, showPreviousClip]);
    return (_jsxs("section", { "aria-label": "Storyteller Clips player", "aria-modal": "true", className: `clip-player clip-player--${layer} ${isClosing
            ? "clip-player--closing"
            : "clip-player--opening"}`, onTouchEnd: (event) => {
            const startY = touchStartYRef.current;
            touchStartYRef.current = null;
            if (startY == null) {
                return;
            }
            const distance = event.changedTouches[0]?.clientY - startY;
            if (distance <= -42) {
                showNextClip();
            }
            else if (distance >= 42) {
                showPreviousClip();
            }
        }, onTouchStart: (event) => {
            touchStartYRef.current =
                event.changedTouches[0]?.clientY ?? null;
        }, ref: dialogRef, role: "dialog", children: [_jsx("div", { "aria-hidden": "true", className: "clip-player__media", "data-clip-id": display.id }), _jsxs("div", { "aria-hidden": "true", className: "clip-player__status", children: [_jsx("span", { children: deviceTime }), _jsx("span", { children: "\u25CF \u25CF \u25FC\uFE0E" })] }), _jsxs("header", { className: "clip-player__top", children: [_jsx("button", { "aria-label": "Back", onClick: requestClose, ref: closeButtonRef, type: "button", children: _jsx(ChevronLeft, { "aria-hidden": "true" }) }), gameScore ? (_jsx(GameScoreBug, { className: "clip-player__game-score", isLiveGame: isLiveGame, onActivate: onOpenGame, score: gameScore })) : (_jsx("strong", { className: "clip-player__game-score", children: companion?.title ?? "NBA Clips" })), _jsx("button", { "aria-label": "Search", type: "button", children: _jsx(Search, { "aria-hidden": "true" }) })] }), _jsxs("aside", { "aria-label": "Clip actions", className: "clip-player__actions", children: [_jsxs("button", { "aria-label": `Follow ${teamCode}`, className: "clip-player__follow", type: "button", children: [_jsx("span", { children: teamCode }), _jsx(Plus, { "aria-hidden": "true" })] }), _jsx("button", { "aria-label": "Like", type: "button", children: _jsx(Heart, { "aria-hidden": "true" }) }), display.media?.likes != null ? (_jsx("small", { children: display.media.likes })) : null, _jsx("button", { "aria-label": "Share", type: "button", children: _jsx(Share2, { "aria-hidden": "true" }) }), display.media?.shares != null ? (_jsx("small", { children: display.media.shares })) : null, _jsx("button", { "aria-label": isMuted ? "Turn sound on" : "Turn sound off", onClick: () => setIsMuted((current) => !current), type: "button", children: isMuted ? (_jsx(VolumeX, { "aria-hidden": "true" })) : (_jsx(Volume2, { "aria-hidden": "true" })) }), _jsx("button", { "aria-label": "Captions", type: "button", children: _jsx(Captions, { "aria-hidden": "true" }) })] }), _jsxs("div", { className: "clip-player__content", children: [_jsx("p", { className: "clip-player__eyebrow", children: teamCode }), display.headline ? _jsx("h2", { children: display.headline }) : null, display.dek ? _jsx("p", { children: display.dek }) : null, _jsx("div", { "aria-hidden": "true", className: "clip-player__progress", children: _jsx("span", {}) }), companion ? (_jsxs("button", { "aria-label": `Go to ${companion.matchup} game screen`, className: "clip-player__destination", onClick: onGoToGame, type: "button", children: [_jsx("span", { children: "Go to game" }), _jsx(ArrowRight, { "aria-hidden": "true" })] })) : null] })] }));
}
